import { apiClient } from "@/lib/api-client";
import type {
  JoinSlackChannelsBody,
  JoinSlackChannelsResp,
  SlackPickerChannelsResp,
} from "@/types/slackChannels.ts";
import { queryOptions } from "@tanstack/react-query";

/**
 * The "add okthx to your channels" picker's requests, for web and native
 * alike. Keyed per workspace, since an account can connect more than one.
 */
export const slackPickerChannelsQueryKey = (teamId: string) =>
  ["slackPickerChannels", teamId] as const;

/** The channels the user is in, and which already have okthx. */
export const slackPickerChannelsQueryOptions = (teamId: string) =>
  queryOptions({
    queryKey: slackPickerChannelsQueryKey(teamId),
    queryFn: async ({ signal }) => {
      const res = await apiClient.get<SlackPickerChannelsResp>(
        "/api/a/connectors/slack/channels",
        { params: { teamId }, signal },
      );
      return res.data;
    },
    // A 404 or 409 is the answer (not connected, reconnect), not a blip.
    retry: false,
    // Each read lists the person's and okthx's channels from Slack, against a
    // rate limit the whole workspace shares. The picker refreshes after a join;
    // focus alone shouldn't.
    refetchOnWindowFocus: false,
  });

/** Queue okthx joining `channelIds`; answers how many it will actually join. */
export const joinSlackChannels = async (body: JoinSlackChannelsBody) => {
  const res = await apiClient.post<JoinSlackChannelsResp>(
    "/api/a/connectors/slack/channels/join",
    body,
  );
  return res.data;
};
